import { useEffect } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Grid,
  Stack,
  TextField,
  Typography,
  Button,
  Divider,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormHelperText,
} from '@mui/material'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useCreateThreadTopicMutation } from 'services/thread'
import { ITender } from 'types/tender'
import { SectionCard } from './section-card'
import { createTopicSchema } from './validations'
import { v4 as uuidv4 } from 'uuid'
import { useSubmittersQuery } from 'services/submissions'
import { EOrganizationType, IUser } from 'types/users'

const CHARACTER_LIMIT = 50 as const

type FormFields = {
  receiverOrganizationId: string
  topicName: string
}

type Props = { tender: ITender; currentUser: IUser }

export const CreateTopicSection = ({ tender, currentUser }: Props) => {
  const { t } = useTranslation()
  const schema = createTopicSchema(t)

  const [createThreadTopic, { isSuccess, isError }] = useCreateThreadTopicMutation()

  const { data: submissionSubmitters } = useSubmittersQuery({ lotId: tender.lot.id })

  const {
    handleSubmit,
    control,
    formState: { isValid },
    watch,
    reset,
  } = useForm<FormFields>({
    defaultValues: {
      receiverOrganizationId:
        currentUser.organizationType === EOrganizationType.CLIENT
          ? tender.organizationId
          : '',
      topicName: '',
    },
    resolver: zodResolver(schema),
  })

  useEffect(() => {
    if (isSuccess) {
      toast.success(t('operation-success'))
      reset()
    }
    if (isError) {
      toast.error(t('failedToCreate'))
    }
  }, [isSuccess, isError, t, reset])

  const topicName = watch('topicName')

  const onSubmit = ({ receiverOrganizationId }: FormFields) => {
    createThreadTopic({
      id: uuidv4(),
      receiverOrganizationId,
      topicName,
      lotId: tender.lot.id,
    })
  }

  return (
    <Box>
      <Grid container spacing={3}>
        {currentUser.organizationType === EOrganizationType.TENDER_AUTHORITY ? (
          <>
            <Grid item xs={6}>
              <SectionCard title={t('lot-name')}>
                <Typography>{tender.lot.name}</Typography>
              </SectionCard>
            </Grid>
            <Grid item xs={6}>
              <SectionCard title={t('contractorList')}>
                <Controller
                  name="receiverOrganizationId"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup {...field}>
                      {submissionSubmitters?.items?.map((contractor) => (
                        <FormControlLabel
                          key={contractor.organizationId}
                          control={<Radio />}
                          value={contractor.organizationId}
                          label={contractor.organizationName}
                        />
                      ))}
                    </RadioGroup>
                  )}
                />
              </SectionCard>
            </Grid>
          </>
        ) : (
          <Grid item xs={12}>
            <Stack spacing={1}>
              <Typography variant="body1" fontWeight="bold">
                {t('lot')}
              </Typography>
              <Box sx={{ padding: '20px', borderRadius: '6px', background: '#fff' }}>
                {tender.name}
              </Box>
            </Stack>
          </Grid>
        )}
        <Grid item xs={12}>
          <Stack spacing={2}>
            <Stack spacing={1}>
              <Typography variant="body1" fontWeight="bold">
                {t('newApplicationCreation')}
              </Typography>
              <Divider />
              <Typography variant="body1">
                {t('newApplicationCreationDescription')}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="flex-start" spacing={2}>
              <Controller
                name="topicName"
                control={control}
                render={({ field }) => (
                  <Stack width="100%">
                    <TextField
                      {...field}
                      label={t('topicName')}
                      size="small"
                      inputProps={{
                        maxLength: CHARACTER_LIMIT,
                      }}
                      fullWidth
                      variant="outlined"
                    />
                    <FormHelperText>{`${topicName.length}/${CHARACTER_LIMIT}`}</FormHelperText>
                  </Stack>
                )}
              />
              <Button
                sx={{ flexShrink: 0 }}
                onClick={handleSubmit(onSubmit)}
                disabled={!isValid}
                variant="contained"
                style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
              >
                {t('createTopic')}
              </Button>
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  )
}
